import React, { Suspense, useState, useEffect } from "react";
import { HashRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { DataProvider } from "./context/DataContext";
import { useData } from "./hooks/useData";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BackgroundProvider, useBackground } from "./context/BackgroundContext";
import { CurrencyResolver } from "./context/CurrencyContext";
import { PortalCountdownProvider } from "./context/PortalCountdownContext";
import { usePortalNotifyCountdown } from "./hooks/usePortalNotifyCountdown";
import { CountdownBar } from "./components/portal/CountdownBar";
import ErrorBoundary from "./components/ErrorBoundary";
import { PageWrapper } from "./components/PageWrapper";
import Sidebar from "./components/Sidebar";
import MobileBottomNav from "./components/MobileBottomNav";
import UpdateBanner from "./components/UpdateBanner";
import UpdateLandedToast from "./components/UpdateLandedToast";
import { UnsavedGuardProvider } from "./context/UnsavedGuardContext";
import PageLoader from "./components/PageLoader";
import { canAccessPageForUser, roleConfig } from "./config/roleConfig";
import { isOwner, isOwnerOnlyPath } from "./config/sidebarItems";
import { useCalendarPrefetch } from "./hooks/data-fetching/useCalendarPrefetch";
import { useDMAnalyticsPrefetch } from "./hooks/data-fetching/useDMAnalyticsPrefetch";
import { useDashboardPrefetch } from "./hooks/data-fetching/useDashboardPrefetch";
import { useIdlePrefetchGate } from "./hooks/data-fetching/useIdlePrefetchGate";
import { useGlassStyle } from "./hooks/useGlassStyle";
import { useTheme } from "./hooks/useTheme";
import { evictStaleCaches } from "./lib/cacheVersion";
import { DateRangeProvider } from "./hooks/useGlobalDateRange";
import type { UserRole } from "./types/core";

// Evict orphaned localStorage caches on app load (runs once, synchronous)
evictStaleCaches();

import { SIDEBAR_COLLAPSED_KEY } from "./components/Sidebar";

// =============================================================================
// APP ROOT
// =============================================================================
// PURPOSE: Top-level component that sets up routing, auth, and layout.
//
// ARCHITECTURE:
// - Context hierarchy: ErrorBoundary > AuthProvider > DataProvider > HashRouter
// - Route protection: RouteGuard enforces roleConfig.ts page access rules
// - Layout logic: AppLayout shows Sidebar for authenticated /admin/* routes
// - Code splitting: React.lazy() for all pages except LoginPage, OperationsDashboard, DMAnalyticsPage, LeaderboardPage
//
// KEY DECISIONS:
// - HashRouter (not BrowserRouter): Works with static hosting (Vercel, GitHub Pages)
// - Non-admin routes (login, register) render immediately while auth loads in background
// - Protected routes show loading spinner if auth not ready yet
// - RouteGuard redirects unauthorized direct URL access to /admin/dashboard
// =============================================================================

// Eager-loaded pages (always needed on initial load or navigated to frequently)
import LoginPage from "./pages/LoginPage";
// Dashboard eagerly imported to prevent React Router v7's startTransition from keeping
// the old page visible during navigation. Trade-off: adds ~600KB to main bundle but
// eliminates the 3s stale-page freeze. The 1.1s parse cost on hard refresh is acceptable
// because hard refreshes are rare vs constant inter-page navigation.
import OperationsDashboard from "./pages/OperationsDashboard";
// LeaderboardPage eagerly imported to prevent React Router v7's startTransition
// from keeping the old page visible during navigation. Same trade-off as
// OperationsDashboard above. The page also holds an active WebSocket
// subscription that, while mounted, prevents new lazy chunks from rendering
// promptly. See docs/handoff/[doing]_NAV_LAG_REALTIME_THRASH.md.
import LeaderboardPage from "./pages/LeaderboardPage";

// Lazy-loaded pages (loaded on demand)
const WebinarHeatmapPage = React.lazy(() => import("./pages/WebinarHeatmapPage"));
// DMAnalyticsPage: eagerly imported (no React.lazy) because it's a primary page.
// Eliminates the ~150ms chunk download delay on first navigation.
import DMAnalyticsPage from "./pages/DMAnalyticsPage";
const CalendlyHeatmapPage = React.lazy(() => import("./pages/CalendlyHeatmapPage"));
const CalendarPage = React.lazy(() => import("./pages/CalendarPage"));
// ARCHIVED: Omnia page removed from routes (Feb 2026) - files preserved for future use
// const OmniaPage = React.lazy(() => import("./pages/OmniaPage"));
const TransactionLogPage = React.lazy(() => import("./pages/TransactionLogPage"));

// Other pages (lighter, can be lazy loaded)
const ClientPortalPage = React.lazy(() => import("./pages/ClientPortalPage"));
const CompletePreview = React.lazy(() => import("./pages/_CompletePreview")); // DEV-only auto-login test harness

const InstallmentTrackingPage = React.lazy(() => import("./pages/InstallmentTrackingPage"));
const InstagramDMPage = React.lazy(() => import("./pages/InstagramDMPage"));
const InvoicePage = React.lazy(() => import("./pages/InvoicePage"));
const PreCallCompliancePage = React.lazy(() => import("./pages/PreCallCompliancePage"));
const LeadMatchReviewPage = React.lazy(() => import("./pages/LeadMatchReviewPage"));
const MastermindDashboard = React.lazy(() => import("./pages/MastermindDashboard"));
const WinnersPage = React.lazy(() => import("./pages/WinnersPage"));
const BackgroundSettingsPage = React.lazy(() => import("./pages/BackgroundSettingsPage"));
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));
const RegistrationLinksPage = React.lazy(() => import("./pages/RegistrationLinksPage"));
const RegistrationWizardPage = React.lazy(() => import("./pages/RegistrationWizardPage"));
const QuizLinkPage = React.lazy(() => import("./pages/QuizLinkPage"));
const RepPortalPage = React.lazy(() => import("./pages/RepPortalPage"));
const SetterLeadTimelinesPage = React.lazy(() => import("./pages/SetterLeadTimelinesPage"));
const LeadSearchPage = React.lazy(() => import("./pages/LeadSearchPage"));
const CallSearchPage = React.lazy(() => import("./pages/CallSearchPage"));
const CallReviewsPage = React.lazy(() => import("./pages/CallReviewsPage"));
const CallReviewDetailPage = React.lazy(() => import("./pages/CallReviewDetailPage"));
const SopLibraryPage = React.lazy(() => import("./pages/SopLibraryPage"));
const SopDetailPage = React.lazy(() => import("./pages/SopDetailPage"));
const LostLeadsPage = React.lazy(() => import("./pages/LostLeadsPage"));
const DialAnalyticsPage = React.lazy(() => import("./pages/DialAnalyticsPage"));
const StoplightsPage = React.lazy(() => import("./pages/StoplightsPage"));
const WorkforcePage = React.lazy(() => import("./pages/WorkforcePage"));
const CoursePage = React.lazy(() => import("./pages/CoursePage"));
const WelcomePage = React.lazy(() => import("./pages/WelcomePage"));
const HandbookPage = React.lazy(() => import("./pages/HandbookPage"));
// Quiz + exec-only Stats tab (QZ-STATS-TAB, 2026-10-09). The stats view lazy-loads inside the hub.
const QuizHubPage = React.lazy(() => import("./pages/QuizHubPage"));
// SidebarViewerPage: eagerly imported (no React.lazy) to avoid the React Router v7
// startTransition stale-page freeze. Page is ~250 lines + zero data fetching, so
// the bundle cost is negligible and dev-tools UX should feel instant.
import SidebarViewerPage from "./pages/SidebarViewerPage";

// ─────────────────────────────────────────────────────────────────────────────
// RouteGuard — blocks access to pages not in roleConfig[role].pages
// ─────────────────────────────────────────────────────────────────────────────
// Safety net: sidebar already hides unauthorized links, but this prevents
// direct URL navigation to restricted pages. Redirects to /admin/dashboard.
const RouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, realUser, isAuthenticated } = useAuth();
  const location = useLocation();

  // Don't guard if not authenticated (AppLayout handles that redirect)
  if (!isAuthenticated || !user) return <>{children}</>;

  // Owner-only pages (ownerOnly in sidebarItems): Mako's login only, checked on
  // realUser so View As never locks him out and never lets anyone else in.
  if (isOwnerOnlyPath(location.pathname) && !isOwner(realUser?.email)) {
    return (
      <Navigate
        to={roleConfig[(user.role as UserRole) || "viewer"].pages[0] || "/admin/dashboard"}
        replace
      />
    );
  }

  const role = (user.role as UserRole) || "viewer";
  const config = roleConfig[role];

  // Role has zero pages (e.g. viewer catch-all) → show "Contact Mako"
  if (config.pages.length === 0) {
    return <NoAccessPage />;
  }

  // Check if the user's role can access this path
  if (!canAccessPageForUser(role, location.pathname, user.id)) {
    // Redirect to first available page for the role
    const fallback = config.pages[0] || "/admin/dashboard";
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};

// Old /admin/quiz-stats links keep their query (e.g. ?preview=1) on the way to the Stats tab. Argus FE 2.
const QuizStatsRedirect: React.FC = () => {
  const params = new URLSearchParams(useLocation().search);
  params.set("view", "stats");
  return <Navigate to={`/admin/quiz?${params.toString()}`} replace />;
};

// ─────────────────────────────────────────────────────────────────────────────
// NoAccessPage — shown when a user's role has no page access
// ─────────────────────────────────────────────────────────────────────────────
const NoAccessPage: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center px-4">
    <div className="max-w-md w-full text-center">
      <p className="text-6xl mb-4">🔒</p>
      <h1 className="text-2xl font-semibold text-glass mb-2">No Access</h1>
      <p className="text-glass-secondary mb-2">
        Your account doesn't have access to any pages yet.
      </p>
      <p className="text-glass-muted token-calendar">
        Contact <span className="text-blue-400 font-medium">Mako</span> if you're seeing this.
      </p>
    </div>
  </div>
);

// Background cache warmers, mounted ~20s after load via useIdlePrefetchGate.
// Mount-gating (not editing the hooks) is deliberate: an unmounted hook can't
// fire its one-shot warm or register its DATE_RANGE_CHANGE_EVENT re-warm
// listener, so every trigger path defers at once. Page-local fetches get the
// 2-core DB to themselves for the first seconds (S55: 10.3s contended vs 0.3s
// clean for the same calendar request). Renders nothing.
const DeferredPrefetchWarmer: React.FC = () => {
  // Silently prefetch current week's calendar data so it loads instantly
  useCalendarPrefetch();

  // Silently prefetch DM Analytics data (setter IDs + metrics) so it loads instantly
  useDMAnalyticsPrefetch();

  // Silently prefetch dashboard KPIs + DM stats so Dashboard loads instantly
  useDashboardPrefetch();

  return null;
};

// Helper component to conditionally render layout
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { currentOrganization } = useData();

  // false until ~20s post-load + a browser idle slot; gates DeferredPrefetchWarmer
  const prefetchReady = useIdlePrefetchGate();

  // Global portal notification countdown bar — mounted ONCE here so it survives
  // route changes. Pages call countdown.start() via usePortalCountdown() context.
  const countdown = usePortalNotifyCountdown(currentOrganization?.id ?? null);

  // Non-admin routes (login, register, 404) don't show Sidebar and render immediately
  const isPublic = !location.pathname.startsWith("/admin");

  // Glass style — light mode only, all routes
  useGlassStyle({ forceLightMode: true });

  // Background context still needed for BackgroundSettingsPage
  useTheme();
  useBackground();

  // Sidebar collapsed state - synced with localStorage
  const [_sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  });

  // Listen for storage changes (when Sidebar toggles the collapsed state)
  // Sidebar dispatches window "storage" event on toggle — no polling needed
  useEffect(() => {
    const handleStorageChange = () => {
      setSidebarCollapsed(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Only show the global loading spinner if we are loading AND trying to access a protected route.
  // Public routes should render immediately (non-blocking), letting auth check happen in background.
  if (isLoading && !isPublic) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-glass-muted">Loading Altara...</p>
        </div>
      </div>
    );
  }

  // If trying to access admin routes while logged out (and not loading), redirect to login
  if (!isLoading && !isAuthenticated && location.pathname.startsWith("/admin")) {
    // Carry the target so a shared link (e.g. a course deep link) survives login.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // Light mode only — always offwhite background
  const bgClass = "bg-offwhite";

  return (
    // UnsavedGuardProvider wraps the whole layout so the version poller (in
    // UpdateBanner) and every route's forms (in {children}) share one dirty
    // registry. Tightest scope that contains both consumers — see
    // context/UnsavedGuardContext.tsx.
    <UnsavedGuardProvider>
      <PortalCountdownProvider value={countdown}>
        <div className={`flex h-screen overflow-hidden relative ${bgClass}`}>
          {/* Only show Sidebar on authenticated /admin/* routes */}
          {isAuthenticated && !isPublic && <Sidebar />}
          {isAuthenticated && !isPublic && <UpdateBanner />}
          {/* Post-reload "update just landed" cue (clean-tab silent reload path) */}
          {isAuthenticated && !isPublic && <UpdateLandedToast />}

          {/* Main Content Area */}
          <main
            className={`flex-1 flex flex-col min-w-0 overflow-hidden relative z-10 ${isAuthenticated && !isPublic ? "pt-12 lg:pt-0" : ""} transition-all duration-200`}
          >
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8">
              {children}
            </div>
          </main>

          {/* Bottom tab bar for mobile navigation (hidden on desktop via lg:hidden) */}
          {isAuthenticated && !isPublic && <MobileBottomNav />}

          {/* Global portal notification countdown bar — mounted once so it survives
              route changes. Pages call countdown.start() via usePortalCountdown().
              Fixed bottom-center, z-[10000] floats above glass paint layers. */}
          <CountdownBar countdown={countdown} orgName={currentOrganization?.name} />

          {/* Background cache warmers — held back until the idle gate opens so
              page-local fetches win the first seconds of DB attention */}
          {prefetchReady && <DeferredPrefetchWarmer />}
        </div>
      </PortalCountdownProvider>
    </UnsavedGuardProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <DateRangeProvider>
          <DataProvider>
            <BackgroundProvider>
              {/* Resolves per-user display currency (profiles.display_currency)
                  from the logged-in user; renders null. Must sit inside
                  AuthProvider — reads realUser. */}
              <CurrencyResolver />
              <HashRouter>
                <AppLayout>
                  <Routes>
                    {/* Root redirects to login (no public landing page) */}
                    <Route path="/" element={<Navigate to="/login" replace />} />
                    <Route path="/login" element={<LoginPage />} />
                    {/* DEV-ONLY — live auto-login test harness (absent from prod builds) */}
                    {import.meta.env.DEV && (
                      <Route
                        path="/register/complete-preview"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <CompletePreview />
                          </Suspense>
                        }
                      />
                    )}
                    {/* Client Registration — Public with magic link validation */}
                    <Route
                      path="/register/:slug"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <RegistrationWizardPage />
                        </Suspense>
                      }
                    />
                    {/* QZ-PHASES — public no-login link quiz, token is the identity */}
                    <Route
                      path="/q/:token"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <QuizLinkPage />
                        </Suspense>
                      }
                    />

                    {/* Protected Routes — all wrapped in RouteGuard for role-based access */}
                    <Route
                      path="/admin/dashboard"
                      element={
                        <RouteGuard>
                          <PageWrapper pageName="Operations Dashboard">
                            <OperationsDashboard />
                          </PageWrapper>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/leaderboard"
                      element={
                        <RouteGuard>
                          <PageWrapper pageName="Leaderboard">
                            <LeaderboardPage />
                          </PageWrapper>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/transactions"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Transaction Log">
                              <TransactionLogPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/workforce"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Workforce">
                              <WorkforcePage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/installments"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Installment Tracking">
                              <InstallmentTrackingPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/heatmap"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Webinar Heatmap">
                              <WebinarHeatmapPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/calendly"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Calendly Analytics">
                              <CalendlyHeatmapPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/stoplights"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Stoplights">
                              <StoplightsPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/precall-compliance"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Pre-Call Compliance">
                              <PreCallCompliancePage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/calendar"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Calendar">
                              <CalendarPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/instagram"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Instagram DM">
                              <InstagramDMPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/dm-analytics"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="DM Analytics">
                              <DMAnalyticsPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/dial-analytics"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Dial Analytics">
                              <DialAnalyticsPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/lead-matches"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Lead Matches">
                              <LeadMatchReviewPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/invoice"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Invoice">
                              <InvoicePage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/mastermind"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Exec Control Center">
                              <MastermindDashboard />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/winners"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Winners">
                              <WinnersPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/background"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Background Settings">
                              <BackgroundSettingsPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/portal"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Client Portal">
                              <ClientPortalPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/rep-portal"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Rep Portal">
                              <RepPortalPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/setter-timelines"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Setter Lead Timelines">
                              <SetterLeadTimelinesPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/lead-search"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Lead Search">
                              <LeadSearchPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/call-search"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Call Search">
                              <CallSearchPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/call-reviews"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Call Reviews">
                              <CallReviewsPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/call-reviews/:reviewId"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Call Review Detail">
                              <CallReviewDetailPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/sop-library"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="SOP Library">
                              <SopLibraryPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/sop-library/:docId"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="SOP Detail">
                              <SopDetailPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/course"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Course">
                              <CoursePage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/handbook"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Handbook">
                              <HandbookPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/quiz"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Quiz">
                              <QuizHubPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    {/* Old Quiz Stats link → the exec-only Stats tab. Non-execs land on the quiz
                        (the tab gate ignores ?view=stats for them). QZ-STATS-TAB, 2026-10-09. */}
                    <Route path="/admin/quiz-stats" element={<QuizStatsRedirect />} />
                    <Route
                      path="/admin/welcome"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Welcome">
                              <WelcomePage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/registration-links"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Registration Links">
                              <RegistrationLinksPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/sidebar-viewer"
                      element={
                        <RouteGuard>
                          <PageWrapper pageName="Sidebar Viewer">
                            <SidebarViewerPage />
                          </PageWrapper>
                        </RouteGuard>
                      }
                    />
                    <Route
                      path="/admin/triage"
                      element={
                        <RouteGuard>
                          <Suspense fallback={<PageLoader />}>
                            <PageWrapper pageName="Lost Leads Triage">
                              <LostLeadsPage />
                            </PageWrapper>
                          </Suspense>
                        </RouteGuard>
                      }
                    />

                    {/* Catch-all: any unmatched route shows 404 */}
                    <Route
                      path="*"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <NotFoundPage />
                        </Suspense>
                      }
                    />
                  </Routes>
                </AppLayout>
              </HashRouter>
            </BackgroundProvider>
          </DataProvider>
        </DateRangeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
